import {Controller} from "@hotwired/stimulus"
import { CarouseInfiniteScrollerMixin } from "../../mixins/carousel_infinite_scroller_mixin"

class CarouselController extends Controller {
  static targets = ["item", "bullet", "wrapper", "instructionsOverlay"]
  static values = {
    activeClass: String,
    autoscroll: Boolean,
    autoscrollInterval: Number,
    instructionsOverlayTimeout: Number
  }

  connect() {
    this.items = this.itemTargets
    if (this.autoscrollValue) {
      this._startAutoscroll()
    }

    this.intersectionObserver = new IntersectionObserver(
      this._handleIntersectionObserver.bind(this),
      {
        root: this.wrapperTarget,
        threshold: 0.75,
      }
    )

    this.items.forEach((item) => this.intersectionObserver.observe(item))

    this._initializeOverlay()

    this._scrollToItem(0, {
      behavior: "instant"
    })
  }

  disconnect() {
    clearInterval(this.interval)
    clearTimeout(this.overlayTimeout)
    this.intersectionObserver.disconnect()
  }

  _startAutoscroll() {
    const interval = this.autoscrollIntervalValue * 1000
    this.interval = setInterval(() => this.next(), interval)
  }

  next(event) {
    event?.preventDefault()
    this._hideOverlay()

    this._scrollToItem((this.currentIndex + 1))
  }

  previous(event) {
    event?.preventDefault()
    this._hideOverlay()

    this._scrollToItem((this.currentIndex - 1))
  }

  goTo(event) {
    event?.preventDefault()
    this._hideOverlay()

    const index = parseInt(event.params.index, 10)
    if (isNaN(index)) return

    this._scrollToItem(index)
  }

  _resetAutoscroll() {
    if (this.autoscrollValue) {
      clearInterval(this.interval)
      this._startAutoscroll()
    }
  }

  _scrollToItem(index, options = {}) {
    const wrapper = this.wrapperTarget
    const { behavior = "smooth" } = options

    wrapper.scrollTo({
      left: this._scrollOffsetWidth(index),
      behavior: behavior,
    })
  }

  _scrollOffsetWidth(index) {
    return this.wrapperTarget.offsetWidth * index
  }

  _handleIntersectionObserver(entries) {
    entries.forEach((entry) => {
      const elem = entry.target
      const elemIndex = this.items.findIndex(
        (item) => item === elem
      )

      if (entry.isIntersecting) {
        if (entry.intersectionRatio >= 0.75) {
          this.bulletTargets[elemIndex]?.classList?.add(
            this.activeClassValue
          )
          this._resetAutoscroll()

          this.currentIndex = elemIndex
        }
      } else {
        this.bulletTargets[elemIndex]?.classList?.remove(
          this.activeClassValue
        )
      }
    })
  }

  // Overlay methods
  _initializeOverlay() {
    if (!this.hasInstructionsOverlayTarget) return

    // Set up auto-hide timeout
    if (this.hasInstructionsOverlayTimeoutValue) {
      this.overlayTimeout = setTimeout(() => {
        this._hideOverlay()
      }, this.instructionsOverlayTimeoutValue * 1000)
    }

    // Add touch/scroll listeners to hide overlay on swipe
    this.wrapperTarget.addEventListener("touchstart", this._hideOverlay.bind(this))
  }

  dismissOverlay(event) {
    event?.preventDefault()
    this._hideOverlay()
  }

  _hideOverlay() {
    if (!this.hasInstructionsOverlayTarget) return

    clearTimeout(this.overlayTimeout)
    this.instructionsOverlayTarget.classList.add("carousel__instructions-overlay--hidden")

    // Remove the overlay from DOM after animation
    setTimeout(() => {
      if (this.hasInstructionsOverlayTarget) {
        this.instructionsOverlayTarget.style.display = "none"
      }
    }, 300)
  }
}

export default class extends CarouseInfiniteScrollerMixin(CarouselController) {}
